import { useState, useEffect } from 'react'
import { getContacts } from '../firebase/contacts'
import './ContactForm.css'

const DealForm = ({
  pipeline,
  onSubmit,
  onCancel,
  initialData = null,
  userId = null,
  contactsUserId = null,
  stageOptions = null
}) => {
  const fetchContactsFor = contactsUserId ?? userId

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    value: initialData?.value || '',
    contactName: initialData?.contactName || '',
    contactEmail: initialData?.contactEmail || '',
    company: initialData?.company || '',
    stage: initialData?.stage || (pipeline?.stages?.[0]?.id || 'lead')
  })
  const [loading, setLoading] = useState(false)
  const [contacts, setContacts] = useState([])
  const [contactSearchTerm, setContactSearchTerm] = useState(initialData?.contactName || '')
  const [showContactDropdown, setShowContactDropdown] = useState(false)
  const [contactsLoading, setContactsLoading] = useState(false)

  const stagesForSelect =
    stageOptions && stageOptions.length > 0 ? stageOptions : pipeline?.stages || []

  useEffect(() => {
    const defaultStage =
      initialData?.stage || pipeline?.stages?.[0]?.id || 'lead'
    setFormData({
      name: initialData?.name ?? '',
      value: initialData == null ? '' : (initialData.value ?? ''),
      contactName: initialData?.contactName ?? '',
      contactEmail: initialData?.contactEmail ?? '',
      company: initialData?.company ?? '',
      stage: defaultStage
    })
    setContactSearchTerm(initialData?.contactName ?? '')
  }, [initialData, pipeline])

  // Fetch contacts for the deal owner (admin editing another rep’s deal uses their contact list)
  useEffect(() => {
    if (!fetchContactsFor) return
    setContactsLoading(true)
    getContacts(fetchContactsFor)
      .then((contactsData) => {
        setContacts(contactsData)
        setContactsLoading(false)
      })
      .catch((error) => {
        console.error('Error fetching contacts:', error)
        setContactsLoading(false)
      })
  }, [fetchContactsFor])

  // Filter contacts based on search term
  const filteredContacts = contacts.filter((contact) => {
    if (!contactSearchTerm) return false
    const searchLower = contactSearchTerm.toLowerCase()
    return (
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.company?.toLowerCase().includes(searchLower)
    )
  })

  const handleContactSelect = (contact) => {
    setFormData({
      ...formData,
      contactName: contact.name || '',
      contactEmail: contact.email || '',
      company: contact.company || ''
    })
    setContactSearchTerm(contact.name || '')
    setShowContactDropdown(false)
  }

  const handleContactNameChange = (e) => {
    const value = e.target.value
    setContactSearchTerm(value)
    setFormData({
      ...formData,
      contactName: value,
      contactEmail: ''
    })
    setShowContactDropdown(value.length > 0)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
    setFormData({
      ...formData,
      [e.target.name]: value
    })
  }

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Deal' : 'New Deal'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Deal Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Q4 Enterprise Deal"
            />
          </div>

          <div className="form-group">
            <label htmlFor="value">Deal Value ($)</label>
            <input
              type="number"
              id="value"
              name="value"
              value={formData.value}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactName">Contact Name</label>
            <div className="contact-lookup-container">
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={contactSearchTerm || formData.contactName}
                onChange={handleContactNameChange}
                onFocus={() => {
                  if (contactSearchTerm || formData.contactName) {
                    setShowContactDropdown(true)
                  }
                }}
                onBlur={() => {
                  // Delay hiding dropdown to allow click on dropdown items
                  setTimeout(() => setShowContactDropdown(false), 200)
                }}
                placeholder="Type to search contacts..."
                autoComplete="off"
              />
              {showContactDropdown && filteredContacts.length > 0 && (
                <div className="contact-dropdown">
                  {filteredContacts.slice(0, 10).map((contact) => (
                    <div
                      key={contact.id}
                      className="contact-dropdown-item"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleContactSelect(contact)
                      }}
                    >
                      <div className="contact-dropdown-name">{contact.name || 'Unnamed'}</div>
                      {contact.company && (
                        <div className="contact-dropdown-company">{contact.company}</div>
                      )}
                      {contact.email && (
                        <div className="contact-dropdown-email">{contact.email}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showContactDropdown && contactSearchTerm && filteredContacts.length === 0 && !contactsLoading && (
                <div className="contact-dropdown">
                  <div className="contact-dropdown-empty">No contacts found</div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="company">Company</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Acme Inc."
            />
          </div>

          {pipeline && (
            <div className="form-group">
              <label htmlFor="stage">Stage</label>
              <select
                id="stage"
                name="stage"
                value={formData.stage}
                onChange={handleChange}
              >
                {stagesForSelect.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DealForm

